import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { captureAndStoreUtms, getStoredUtms, trackEvent } from "@nusrif/services";
import { trackMetaPixel } from "@nusrif/services";
import { StatusBar, Style } from "@capacitor/status-bar";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";

const ScrollToTop = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const startTimeRef = useRef<number>(Date.now());
  const isPageVisibleRef = useRef<boolean>(true);
  const activeTimeRef = useRef<number>(0);
  const lastActiveTimeRef = useRef<number>(Date.now());
  const hasTrackedTimeRef = useRef<boolean>(false);

  // Track time spent on page
  const trackTimeSpent = (eventType: string = "navigation") => {
    if (hasTrackedTimeRef.current) return; // Prevent double tracking

    const currentTime = Date.now();
    const timeSpent = Math.round((currentTime - startTimeRef.current) / 1000); // Convert to seconds

    if (timeSpent > 0) {
      // Use sendBeacon for beforeunload to ensure data is sent
      const trackingData = {
        event: "time_spent_on_page",
        properties: {
          path: location.pathname,
          title: document.title,
          time_spent_seconds: timeSpent,
          active_time_seconds: Math.round(activeTimeRef.current / 1000),
          page_visible_percentage: Math.round(
            (activeTimeRef.current / (currentTime - startTimeRef.current)) * 100
          ),
          exit_type: eventType,
        },
      };

      if (eventType === "beforeunload") {
        // Use sendBeacon for reliable data transmission on page unload
        if (navigator.sendBeacon) {
          navigator.sendBeacon("/api/track", JSON.stringify(trackingData));
        } else {
          // Fallback to synchronous XMLHttpRequest (less reliable)
          const xhr = new XMLHttpRequest();
          xhr.open("POST", "/api/track", false);
          xhr.setRequestHeader("Content-Type", "application/json");
          xhr.send(JSON.stringify(trackingData));
        }
      } else {
        // Use normal trackEvent for navigation
        trackEvent(trackingData);
      }

      hasTrackedTimeRef.current = true;
    }
  };

  // Track page view and start timing
  useEffect(() => {
    window.scrollTo(0, 0);

    // --- store UTMs if present ---
    captureAndStoreUtms(location.search);

    const utms = getStoredUtms();

    if (Capacitor.isNativePlatform()) {
      // if location.pathname is not '/' change status bar style to white
      if (location.pathname !== "/") {
        StatusBar.setStyle({ style: Style.Light });
        StatusBar.setBackgroundColor({ color: "#ffffff" });
        EdgeToEdge.setBackgroundColor({ color: "#ffffff" });
      }

      // For home page, set status bar style to dark and background color
      if (location.pathname === "/") {
        StatusBar.setStyle({ style: Style.Dark });
        StatusBar.setBackgroundColor({ color: "#6A7A7D" });
        EdgeToEdge.setBackgroundColor({ color: "#6A7A7D" });
      }
    }

    // Reset timing for new page
    startTimeRef.current = Date.now();
    activeTimeRef.current = 0;
    lastActiveTimeRef.current = Date.now();
    isPageVisibleRef.current = true;
    hasTrackedTimeRef.current = false;


    // Track page visit (internal analytics)
    trackEvent({
      event: "page_view",
      properties: {
        path: location.pathname,
        title: document.title,
        search: location.search,
        hash: location.hash,
        utms: utms,
      },
    });

    // Track page view with Meta Pixel
    trackMetaPixel("PageView");

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      const currentTime = Date.now();

      if (document.hidden) {
        // Page became hidden, update active time
        if (isPageVisibleRef.current) {
          activeTimeRef.current += currentTime - lastActiveTimeRef.current;
          isPageVisibleRef.current = false;
        }
      } else {
        // Page became visible, update last active time
        lastActiveTimeRef.current = currentTime;
        isPageVisibleRef.current = true;
      }
    };

    // Handle user activity (mouse movement, clicks, scrolls, key presses)
    const handleUserActivity = () => {
      if (isPageVisibleRef.current) {
        const currentTime = Date.now();
        activeTimeRef.current += currentTime - lastActiveTimeRef.current;
        lastActiveTimeRef.current = currentTime;
      }
    };

    // Handle page unload events (browser close, refresh, etc.)
    const handleBeforeUnload = () => {
      trackTimeSpent("beforeunload");
    };

    const handlePageHide = () => {
      trackTimeSpent("pagehide");
    };

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("mousemove", handleUserActivity);
    document.addEventListener("click", handleUserActivity);
    document.addEventListener("scroll", handleUserActivity);
    document.addEventListener("keypress", handleUserActivity);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    // Cleanup function to track time when leaving page
    return () => {
      // Remove event listeners
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("mousemove", handleUserActivity);
      document.removeEventListener("click", handleUserActivity);
      document.removeEventListener("scroll", handleUserActivity);
      document.removeEventListener("keypress", handleUserActivity);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);

      // Track time spent on this page (for navigation)
      trackTimeSpent("navigation");
    };
  }, [location]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let backHandler: PluginListenerHandle | null = null;

    const setupBackHandler = async () => {
      backHandler = await CapacitorApp.addListener("backButton", () => {
        if (location.pathname !== "/") {
          navigate(-1);
        } else {
          CapacitorApp.exitApp();
        }
      });
    };

    setupBackHandler();

    return () => {
      if (backHandler) {
        backHandler.remove();
      }
    };
  }, [location.pathname, navigate]);

  return null;
};

export default ScrollToTop;
